import { useCallback, useState, useEffect, useMemo } from 'react';
import { ColumnOption } from './types';
import {
  useDataFeedWizardStore,
  useDirtyTracking,
} from '../../../../store/dataFeedWizard';
import Actions from '../../../../store/Actions';
import {
  ColumnMappingRequest,
  FileColumnsResponse,
} from '../../../../types/generatedTypes';
import { VALIDATION_MESSAGES } from './constants';

export const useConfigureMappingStep = () => {
  // Get store data and setters
  const {
    configureMappingData,
    setConfigureMappingData,
    addOTCColumn,
    removeOTCColumn,
    updateOTCColumn,
    updateOTCColumnIdentifier,
    setCurrentStepButtonState,
    registerStepActions,
    clearStepActions,
    sessionId,
    setStepLoading,
    setPristineState,
    vaultFiles,
    excelFiles,
    restoredFiles,
  } = useDataFeedWizardStore();

  const { isDirty } = useDirtyTracking();

  const [columnOptions, setColumnOptions] = useState<ColumnOption[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const [validationErrors, setValidationErrors] = useState<{
    accountNumberColumn?: boolean;
    isinColumn?: boolean;
    symbolColumn?: boolean;
  }>({});

  // Load file columns on mount
  useEffect(() => {
    const loadColumns = async (): Promise<void> => {
      if (!sessionId) {
        console.warn('Missing sessionId - cannot load columns');
        return;
      }

      setStepLoading(true);
      try {
        const response: FileColumnsResponse =
          await Actions.getWizardFileColumns(sessionId);
        const columns = response.columns
          .filter(col => !col.startsWith('_'))
          .map(col => ({
            value: col,
            label: col,
          }));
        setColumnOptions(columns);
      } catch (error) {
        console.error('Failed to load columns:', error);
        setColumnOptions([]);
      } finally {
        setStepLoading(false);
      }
    };

    loadColumns();
  }, [sessionId]);

  // Auto-select columns for vault uploads
  useEffect(() => {
    // Detect vault mode from API data
    const isVaultMode =
      vaultFiles.length > 0 ||
      restoredFiles.some(f => f.upload_tab === 'vaults');

    // Only auto-select for vault mode
    if (!isVaultMode) return;

    // Only on initial load (no saved selections for any field)
    if (
      configureMappingData.isinColumn ||
      configureMappingData.symbolColumn ||
      configureMappingData.accountNumberColumn
    )
      return;

    // Need columns to be loaded
    if (columnOptions.length === 0) return;

    // Check if columns exist
    const hasIsin = columnOptions.some(col => col.value === 'ISIN');
    const hasSymbol = columnOptions.some(col => col.value === 'Symbol');
    const hasAccountNumber = columnOptions.some(
      col => col.value === 'מספר חשבון'
    );
    const hasSecurityNumber = columnOptions.some(
      col => col.value === 'מספר נייר'
    );

    // Auto-select if all required columns exist
    if (hasIsin && hasSymbol && hasAccountNumber) {
      setConfigureMappingData({
        isinColumn: 'ISIN',
        symbolColumn: 'Symbol',
        accountNumberColumn: 'מספר חשבון',
        // Auto-select "מספר נייר" as OTC column if it exists
        otcColumns: hasSecurityNumber
          ? [{ column: 'מספר נייר', isIdentifier: false }]
          : [{ column: '', isIdentifier: false }],
      });
    }
  }, [
    columnOptions,
    vaultFiles,
    restoredFiles,
    configureMappingData.isinColumn,
    configureMappingData.symbolColumn,
    configureMappingData.accountNumberColumn,
    setConfigureMappingData,
  ]);

  const handleAccountNumberColumnChange = (
    value: string | number | null
  ): void => {
    const stringValue =
      typeof value === 'string' ? value : value === null ? null : String(value);
    setConfigureMappingData({ accountNumberColumn: stringValue });
    // Clear validation error when field is changed
    if (stringValue) {
      setValidationErrors(prev => ({
        ...prev,
        accountNumberColumn: false,
      }));
    }
  };

  const handleIsinColumnChange = (value: string | number | null): void => {
    const stringValue =
      typeof value === 'string' ? value : value === null ? null : String(value);
    setConfigureMappingData({ isinColumn: stringValue });
    // Clear validation error when field is changed
    if (stringValue) {
      setValidationErrors(prev => ({
        ...prev,
        isinColumn: false,
      }));
    }
  };

  const handleSymbolColumnChange = (value: string | number | null): void => {
    const stringValue =
      typeof value === 'string' ? value : value === null ? null : String(value);
    setConfigureMappingData({ symbolColumn: stringValue });
    // Clear validation error when field is changed
    if (stringValue) {
      setValidationErrors(prev => ({
        ...prev,
        symbolColumn: false,
      }));
    }
  };

  const handleAddOTCColumn = (): void => {
    addOTCColumn();
  };

  const handleRemoveOTCColumn = (index: number): void => {
    removeOTCColumn(index);
  };

  const handleOTCColumnChange = (
    index: number,
    value: string | number | null
  ): void => {
    const stringValue =
      typeof value === 'string' ? value : value === null ? '' : String(value);
    updateOTCColumn(index, stringValue);
    // Reset identifier to false when column is cleared
    if (!stringValue) {
      updateOTCColumnIdentifier(index, false);
    }
  };

  // OTC section is only enabled when both ISIN and Symbol columns are selected
  const isOTCSectionEnabled =
    !!configureMappingData.isinColumn && !!configureMappingData.symbolColumn;

  // Determine if this is an Excel upload (for showing identifier toggle)
  // Check actual uploaded files, not just which tab is selected
  const isExcelUpload = useMemo(() => {
    const hasExcelFiles = excelFiles.length > 0;
    const hasRestoredExcelFiles = restoredFiles.some(
      f => f.upload_tab === 'excel'
    );
    return hasExcelFiles || hasRestoredExcelFiles;
  }, [excelFiles, restoredFiles]);

  const handleOTCColumnIdentifierChange = (
    index: number,
    isIdentifier: boolean
  ) => {
    updateOTCColumnIdentifier(index, isIdentifier);
  };

  const availableIsinColumns = useMemo((): ColumnOption[] => {
    const excludedColumns = [
      configureMappingData.accountNumberColumn,
      configureMappingData.symbolColumn,
    ].filter(Boolean) as string[];

    return columnOptions.filter(col => !excludedColumns.includes(col.value));
  }, [
    columnOptions,
    configureMappingData.accountNumberColumn,
    configureMappingData.symbolColumn,
  ]);

  const availableSymbolColumns = useMemo((): ColumnOption[] => {
    const excludedColumns = [
      configureMappingData.accountNumberColumn,
      configureMappingData.isinColumn,
    ].filter(Boolean) as string[];

    return columnOptions.filter(col => !excludedColumns.includes(col.value));
  }, [
    columnOptions,
    configureMappingData.accountNumberColumn,
    configureMappingData.isinColumn,
  ]);

  const getAvailableOTCColumns = (
    currentIndex: number
  ): { value: string; label: string }[] => {
    // Get all selected OTC columns except the current index
    const selectedColumns = configureMappingData.otcColumns
      .filter((col, idx) => idx !== currentIndex && col.column !== '')
      .map(col => col.column);

    // Also exclude ISIN and Symbol columns from OTC options
    const excludedColumns = [
      ...selectedColumns,
      configureMappingData.isinColumn,
      configureMappingData.symbolColumn,
    ].filter(Boolean) as string[];

    // Filter out already selected columns from available options (use all loaded columns)
    const allColumnNames = columnOptions.map(col => col.value);
    const availableColumnNames = allColumnNames.filter(
      col => !excludedColumns.includes(col)
    );

    return availableColumnNames.map(col => ({ value: col, label: col }));
  };

  // Memoize validateFields to prevent infinite loops in action registration useEffect
  const validateFields = useCallback((): boolean => {
    const { accountNumberColumn, isinColumn, symbolColumn } =
      configureMappingData;

    const errors = {
      accountNumberColumn: !accountNumberColumn,
      isinColumn: !isinColumn,
      symbolColumn: !symbolColumn,
    };

    // Check for duplicates
    const selected = [accountNumberColumn, isinColumn, symbolColumn].filter(
      Boolean
    );
    if (new Set(selected).size !== selected.length) {
      console.error(VALIDATION_MESSAGES.DUPLICATE_SELECTION);
      return false;
    }

    setValidationErrors(errors);
    return (
      !errors.accountNumberColumn && !errors.isinColumn && !errors.symbolColumn
    );
  }, [configureMappingData]);

  // Save column mapping to backend
  const handleNext = useCallback(async (): Promise<boolean | number> => {
    if (!validateFields()) {
      return false;
    }

    if (!sessionId) {
      console.error('Missing sessionId - cannot save column mapping');
      return false;
    }

    // Skip save if no changes were made
    if (!isDirty('step1')) {
      console.log('ConfigureMapping: No changes detected, skipping save');
      return true; // Always go to LinkAccountsStep (no skipping)
    }

    setIsSaving(true);
    try {
      const request: ColumnMappingRequest = {
        accountNumberColumn: configureMappingData.accountNumberColumn!,
        isinColumn: configureMappingData.isinColumn!,
        symbolColumn: configureMappingData.symbolColumn!,
        otcColumns: configureMappingData.otcColumns.filter(
          col => col.column !== ''
        ),
      };

      await Actions.saveWizardColumnMapping(sessionId, request);

      // Update pristine state after successful save
      setPristineState('step1', configureMappingData);

      return true; // Always go to LinkAccountsStep (no skipping)
    } catch (error) {
      console.error('Failed to save column mapping:', error);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [
    validateFields,
    sessionId,
    configureMappingData,
    isDirty,
    setPristineState,
  ]);

  // Update store with validation state whenever dependencies change
  // Note: setCurrentStepButtonState is omitted from deps - Zustand store functions are stable
  useEffect(() => {
    const hasAccountNumber = !!configureMappingData.accountNumberColumn;
    const hasIsin = !!configureMappingData.isinColumn;
    const hasSymbol = !!configureMappingData.symbolColumn;
    const isValid = hasAccountNumber && hasIsin && hasSymbol;

    let validationMessage: string | undefined;
    if (!isValid) {
      const missingFields: string[] = [];
      if (!hasAccountNumber) missingFields.push('Account Number Column');
      if (!hasIsin) missingFields.push('ISIN Column');
      if (!hasSymbol) missingFields.push('Symbol Column');
      validationMessage = `Please select the following required fields: ${missingFields.join(', ')}`;
    }

    setCurrentStepButtonState({
      disabled: !isValid,
      loading: isSaving,
      validationMessage,
    });
    // Note: setCurrentStepButtonState is omitted from deps - Zustand store functions are stable
  }, [
    configureMappingData.accountNumberColumn,
    configureMappingData.isinColumn,
    configureMappingData.symbolColumn,
    isSaving,
  ]);

  // Register step actions when component mounts
  // Note: registerStepActions and clearStepActions are omitted from deps - Zustand store functions are stable
  useEffect(() => {
    registerStepActions({
      onNext: handleNext,
      validateFields,
    });

    return () => {
      clearStepActions();
    };
  }, [handleNext, validateFields]);

  return {
    columnOptions,
    configureMappingData,
    handleAccountNumberColumnChange,
    handleIsinColumnChange,
    handleSymbolColumnChange,
    handleAddOTCColumn,
    handleRemoveOTCColumn,
    handleOTCColumnChange,
    handleOTCColumnIdentifierChange,
    getAvailableOTCColumns,
    availableIsinColumns,
    availableSymbolColumns,
    validationErrors,
    validateFields,
    isSaving,
    isOTCSectionEnabled,
    isExcelUpload,
  };
};
